import React, { useState } from "react";
import { useContent } from "../context/ContentContext";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Lightbox } from "../components/Lightbox";
import { Expand, Play } from "lucide-react";

export default function OurStory() {
  const { content } = useContent();
  const s = content?.story;
  const [lightboxIndex, setLightboxIndex] = useState(null);
  if (!s) return null;
  const sections = [
    { title: "How We Met", body: s.how_we_met },
    { title: "Our Relationship", body: s.relationship },
    { title: "The Proposal", body: s.proposal },
    { title: "What We're Looking Forward To Most", body: s.looking_forward },
  ];
  const imgs = (s.images || []).filter(Boolean);
  const gallery = s.gallery || [];
  const allPhotos = [...imgs, ...gallery];

  return (
    <div data-testid="our-story-page">
      <PageHero overline="Our Journey" title="Our Story" subtitle="Every love story is beautiful, but ours is our favorite." />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 space-y-24">
        {sections.map((sec, i) => (
          <Reveal key={sec.title}>
            <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
              <div className="[direction:ltr]">
                <p className="overline text-olive">Chapter {String(i + 1).padStart(2, "0")}</p>
                <h2 className="font-serif text-4xl sm:text-5xl mt-3 text-matte">{sec.title}</h2>
                <hr className="gold-divider my-6" />
                <p className="text-taupe leading-relaxed text-lg whitespace-pre-line">{sec.body}</p>
                {sec.title === "The Proposal" && (
                  <a href="https://www.instagram.com/reel/DUtLoBBEZtVSTkMHzt0ZukXuEPzKZBrs05olkE0/"
                    target="_blank" rel="noopener noreferrer"
                    className="btn-olive rounded-full px-7 py-3 overline inline-flex items-center gap-2 mt-6"
                    data-testid="proposal-video-btn">
                    <Play size={16} /> Click Here to See Proposal
                  </a>
                )}
              </div>
              <div className="[direction:ltr] overflow-hidden rounded-lg">
                {imgs[i] && (
                  <button
                    type="button" onClick={() => setLightboxIndex(i)}
                    className="group relative block w-full cursor-zoom-in overflow-hidden rounded-lg"
                    aria-label={`View ${sec.title} photo full screen`} data-testid={`story-image-${i}`}
                  >
                    <img src={imgs[i]} alt={sec.title} loading="lazy"
                      className="w-full h-[420px] object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                    <span className="absolute inset-0 bg-matte/0 group-hover:bg-matte/15 transition-colors duration-300" />
                    <span className="absolute bottom-4 right-4 bg-soft-white/90 text-matte rounded-full p-2.5 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                      <Expand size={16} />
                    </span>
                  </button>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {gallery.length > 0 && (
        <section className="stone-panel border-t border-taupe/30 py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <p className="overline text-olive">Captured Moments</p>
                <h2 className="font-serif text-4xl sm:text-5xl mt-2 text-matte">A Few of Our Favorites</h2>
                <div className="gold-line mx-auto mt-6" />
                <p className="mt-4 text-taupe text-sm">Tap any photo to view it full screen.</p>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6" data-testid="story-gallery">
              {gallery.map((src, i) => (
                <Reveal key={i} delay={(i % 3) * 90}>
                  <button
                    type="button" onClick={() => setLightboxIndex(imgs.length + i)}
                    className={`group relative block w-full cursor-zoom-in overflow-hidden rounded-lg ${i % 5 === 0 ? "md:row-span-2" : ""}`}
                    aria-label={`View favorite photo ${i + 1} full screen`} data-testid={`gallery-image-${i}`}
                  >
                    <img src={src} alt={`Vernetta and Quin ${i + 1}`} loading="lazy"
                      className="w-full h-full min-h-[220px] object-cover group-hover:scale-[1.05] transition-transform duration-700" />
                    <span className="absolute inset-0 bg-matte/0 group-hover:bg-matte/20 transition-colors duration-300" />
                    <span className="absolute bottom-3 right-3 bg-soft-white/90 text-matte rounded-full p-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                      <Expand size={15} />
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Lightbox photos={allPhotos} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onIndex={setLightboxIndex} />
    </div>
  );
}
